import {createContext, useContext, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject} from "react";
import {ArrowLeft, ArrowRight, X} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import type Combatant from "@/encounter/model/Combatant.ts";
import {Badge} from "@/components/ui/badge.tsx";
import CombatantDetailContent from "./CombatantDetailContent.tsx";

export type CombatantDetail = "actions" | "traits" | "character" | "spells";
type Side = "left" | "right";
const titles: Record<CombatantDetail, string> = {actions: "Aktionen", traits: "Merkmale", character: "Charakterdetails", spells: "Zauber"};
type DockState = Partial<Record<Side, CombatantDetail>>;
const DockContext = createContext<{
    panels: DockState; toggle: (detail: CombatantDetail, side?: Side) => void; id: string;
} | null>(null);
export const useCombatantDock = () => useContext(DockContext);

export function CombatantDockLayout({combatant, children, mainPanelRef, onLayoutChange}: {
    combatant?: Combatant; children: ReactNode; mainPanelRef?: RefObject<HTMLDivElement | null>;
    onLayoutChange?: () => void;
}) {
    const [panels, setPanels] = useState<DockState>({});
    const [expandedActions, setExpandedActions] = useState<Record<string, boolean>>({});
    const lastSide = useRef<Partial<Record<CombatantDetail, Side>>>({});
    const triggers = useRef<Partial<Record<Side, HTMLElement>>>({});
    const id = useId();
    // Reposition after the dock DOM changes, before the browser paints its new width.
    useLayoutEffect(() => {onLayoutChange?.();}, [panels, combatant, onLayoutChange, expandedActions]);
    const toggle = (detail: CombatantDetail, requestedSide?: Side) => {
        const side = requestedSide ?? lastSide.current[detail] ?? "right";
        if (document.activeElement instanceof HTMLElement) triggers.current[side] = document.activeElement;
        lastSide.current[detail] = side;
        setPanels(current => {
            if (current[side] === detail) return {...current, [side]: undefined};
            const next = {...current};
            if (next.left === detail) delete next.left;
            if (next.right === detail) delete next.right;
            return {...next, [side]: detail};
        });
    };
    const close = (side: Side) => {
        setPanels(current => ({...current, [side]: undefined}));
        requestAnimationFrame(() => triggers.current[side]?.focus());
    };
    const visiblePanels = combatant ? panels : {};
    const panel = (side: Side) => {
        const detail = visiblePanels[side];
        if (!detail || !combatant) return null;
        return <section key={side} id={`${id}-${side}`} aria-label={`${titles[detail]} – ${combatant.name}`}
            className="relative flex min-h-0 w-80 shrink-0 flex-col bg-gray-950"
            onKeyDown={event => {if (event.key === "Escape") {event.stopPropagation(); close(side);}}}>
            <Button variant="ghost" size="icon-sm" className="absolute right-3 top-1 z-10 bg-gray-950/90"
                aria-label={`${titles[detail]} schließen`} title={`${titles[detail]} schließen`}
                onClick={() => close(side)}><X/></Button>
            <div className="min-h-0 overflow-y-auto overscroll-contain break-words py-3">
                <CombatantDetailContent key={`${combatant.id}-${detail}`} combatant={combatant} detail={detail}
                    expandedActions={expandedActions}
                    onExpansionChange={entries => setExpandedActions(current => ({...current, ...entries}))}/>
            </div>
        </section>;
    };
    return <DockContext.Provider value={{panels, toggle, id}}>
        <div className="flex min-h-0 divide-x divide-white/15 text-sm text-foreground"
            style={{width: 384 + 320 * Object.values(visiblePanels).filter(Boolean).length}}>
            {panel("left")}
            <div ref={mainPanelRef} className="min-h-0 w-96 shrink-0 overflow-y-auto overscroll-contain">
                {children}
            </div>
            {panel("right")}
        </div>
    </DockContext.Provider>;
}

export function CombatantDetailButton({detail, count, countLabel}: {detail: CombatantDetail; count?: number; countLabel?: string}) {
    const dock = useCombatantDock();
    if (!dock) return null;
    return <div className="flex min-w-0 items-stretch rounded-lg border bg-surface/30">
        {(["left", "center", "right"] as const).map(side => {
            const active = side === "center" ? Object.values(dock.panels).includes(detail) : dock.panels[side] === detail;
            return <Button key={side} type="button" variant={active ? "secondary" : "ghost"}
                className={side === "center" ? "h-9 min-w-0 flex-1 justify-start gap-2 px-2" : "h-9 shrink-0 px-2"}
                aria-label={side === "center" ? `${titles[detail]} öffnen` : `${titles[detail]} ${side === "left" ? "links" : "rechts"} öffnen`}
                aria-expanded={active} aria-controls={side === "center" ? undefined : `${dock.id}-${side}`}
                onClick={() => dock.toggle(detail, side === "center" ? undefined : side)}>
                {side === "center" ? <><span className="font-semibold">{titles[detail]}</span>
                    {count != undefined && <Badge variant="outline" className="ml-auto" aria-label={countLabel ?? `${count} ${titles[detail]}`} title={countLabel}>{count}</Badge>}</>
                    : side === "left" ? <ArrowLeft aria-hidden="true"/> : <ArrowRight aria-hidden="true"/>}
            </Button>;
        })}
    </div>;
}
